/**
 * A FILA DE COBRANÇA por motivo (aba Financeiro › Cobrança, decisão E1 do plano de 30/10/2026).
 * Prévia: https://claude.ai/artifact/E1gGoqTdCgJerRZUZb4nUA
 *
 * Regras puras: o que separa a fila mora aqui, e a tela só desenha. A linha vem do
 * `GET /invoices/dunning-candidates?motivos=1` (backend #569), que traz os seis motivos em campos — a tela não lê
 * frase (`blockedReason`) para extrair dado.
 *
 * As três decisões da prévia (P, Q, T) estão com o Heitor; a tela nasce nos RECOMENDADOS (P1 Q1 T1). P e Q são
 * constantes abaixo, para a troca ser de uma linha; T está descrita junto delas.
 */
import { fmtCurrency, fmtDate } from './formatters';

/** P: fatura que cabe em mais de um motivo. P1 = só no mais urgente, os outros viram selo; P2 = em todos os grupos. */
export const DECISAO_P = 'mais-urgente' as 'mais-urgente' | 'todos';
/** Q: boleto baixado sem pausa. Q1 = grupo próprio, com Pausar; Q2 = junto do "Boleto vivo vencido", com selo. */
export const DECISAO_Q = 'grupo-proprio' as 'grupo-proprio' | 'junto-do-boleto-vivo';
/* T: o "Cobrar" da linha. Até 30/10/2026 era T1 (abria Faturas e o envio era o modal de lá). Com a unificação (E1) o
   envio mora na própria Cobrança: o "Cobrar" abre o ModalDeCobranca, e Faturas não cobra mais. */

export interface LinhaDaCobranca {
  id: number;
  code: string;
  clientId: number;
  clientName: string;
  dueDate: string;
  daysOverdue: number;
  /** Saldo em aberto (fatura Parcial cobra o que falta). */
  originalValue: number;
  paymentMode?: 'boleto' | 'pix';
  boletoStatus?: string | null;
  dunningCount?: number;
  /** Instante da última cobrança enviada (ISO). Vai no selo "Cobrada N× · dd/mm". */
  lastDunningAt?: string | null;
  snoozedUntil?: string | null;
  snoozeReason?: string | null;
  blockedKind?: string;
  blockedReason?: string;
  blockedUntil?: string | null;
  // ── os campos de `?motivos=1`
  notasAutorizadas?: number;
  entregaPendente?: boolean;
  promessaVencida?: { date: string; amount: number | null } | null;
  promisedDate?: string | null;
  promisedAmount?: number | null;
  // ── desde o backend #587 vêm em toda linha, sem `?motivos=1`
  isPublicSector?: boolean;
  paymentMethod?: string | null;
  /** A faixa de atraso calculada pelo servidor, com as chaves do `aging` do resumo. `null` = ainda não venceu. */
  faixaDeAtraso?: FaixaDeAtraso | null;
}

export type Motivo = 'email' | 'semNota' | 'promessaVencida' | 'pausada' | 'boletoVivo' | 'orgao' | 'tedPrivado' | 'baixado' | 'outras';

/**
 * A ordem de URGÊNCIA (P1): a fatura cai no primeiro motivo em que cabe.
 *
 * ⚠️ `pausada` vem ANTES dos motivos de cobrar (boleto vivo, órgão, baixado), e não por último como na lista de
 * grupos: pausa e promessa são decisão de alguém de não cobrar até a data. Depois dela, a fatura mostrada em "Boleto
 * vivo" mandaria cobrar quem prometeu pagar. E é isso que faz a pausada "voltar sozinha para o grupo de origem" na data:
 * a elegibilidade deixa de devolver `silenciada`/`promessa` e a fatura cai no motivo seguinte.
 * Os três primeiros vencem a pausa porque não são cobrança: sem nota e e-mail devolvido pedem conserto, e a promessa
 * vencida é justamente a que acabou.
 */
export const PRIORIDADE: Motivo[] = ['email', 'semNota', 'promessaVencida', 'pausada', 'boletoVivo', 'orgao', 'tedPrivado', 'baixado', 'outras'];

/** A ordem dos GRUPOS na tela, a da prévia: o que o cliente não consegue pagar primeiro, o que espera a data no fim. */
export const ORDEM_DOS_GRUPOS: Motivo[] = ['email', 'semNota', 'promessaVencida', 'boletoVivo', 'orgao', 'tedPrivado', 'baixado', 'pausada', 'outras'];

const CABE: Record<Motivo, (l: LinhaDaCobranca) => boolean> = {
  email: (l) => !!l.entregaPendente,
  semNota: (l) => l.notasAutorizadas === 0,
  promessaVencida: (l) => !!l.promessaVencida,
  pausada: (l) => l.blockedKind === 'silenciada' || l.blockedKind === 'promessa',
  // `paymentMode = 'boleto'` é o `boletoVivo()` do backend: título registrado ou confirmado no banco.
  boletoVivo: (l) => l.paymentMode === 'boleto' || (DECISAO_Q === 'junto-do-boleto-vivo' && l.boletoStatus === 'baixado'),
  // T1 (30/10/2026): órgão é o SETOR PÚBLICO do cadastro. A empresa privada que paga por TED (a GEO BIO GAS) tem grupo
  // próprio e é cobrada por e-mail, como empresa: não tem fiscal de contrato nem ofício.
  orgao: (l) => !!l.isPublicSector,
  tedPrivado: (l) => !l.isPublicSector && String(l.paymentMethod || '').toLowerCase() === 'ted',
  baixado: (l) => DECISAO_Q === 'grupo-proprio' && l.boletoStatus === 'baixado',
  outras: () => true,
};

/** Todos os motivos em que a fatura cabe, na ordem de urgência (`outras` só quando nenhum outro cabe). */
export function motivosDaFatura(l: LinhaDaCobranca): Motivo[] {
  const m = PRIORIDADE.filter((k) => k !== 'outras' && CABE[k](l));
  return m.length ? m : ['outras'];
}

export interface GrupoDaFila {
  motivo: Motivo;
  linhas: Array<{ linha: LinhaDaCobranca; selos: Motivo[] }>;
  total: number;
}

/** Separa a fila em grupos, na ordem da tela. Grupos vazios vêm também: a tela diz que estão vazios. */
export function agruparFila(linhas: LinhaDaCobranca[], modo: typeof DECISAO_P = DECISAO_P): GrupoDaFila[] {
  const grupos = new Map<Motivo, GrupoDaFila>(ORDEM_DOS_GRUPOS.map((m) => [m, { motivo: m, linhas: [], total: 0 }]));
  for (const linha of linhas) {
    const motivos = motivosDaFatura(linha);
    const onde = modo === 'todos' ? motivos : motivos.slice(0, 1);
    for (const m of onde) {
      const g = grupos.get(m)!;
      g.linhas.push({ linha, selos: motivos.filter((x) => x !== m) });
      g.total += Number(linha.originalValue) || 0;
    }
  }
  return ORDEM_DOS_GRUPOS.map((m) => grupos.get(m)!);
}

export type TomDoGrupo = 'danger' | 'warning' | null;

/** O ícone de cada motivo, do mapa da casa (shared/Icon). Padrão da Central: peso, ícone e barra. */
export const GRUPO: Record<Motivo, { titulo: string; curto: string; icone: string; sub: string; tom: TomDoGrupo; selo: string; vazio: string }> = {
  email: {
    titulo: 'E-mail não chegou', curto: 'E-mail não chegou', icone: 'send', tom: 'danger', selo: 'e-mail voltou',
    sub: 'O e-mail da fatura voltou. Reenvie para outro contato do cliente.',
    vazio: 'Nenhuma vencida tem e-mail devolvido.',
  },
  semNota: {
    titulo: 'Sem nota fiscal', curto: 'Sem nota fiscal', icone: 'fileText', tom: 'danger', selo: 'sem nota',
    sub: 'Sem nota, o cliente não consegue pagar e o e-mail da fatura não sai.',
    vazio: 'Todas as vencidas têm nota autorizada.',
  },
  promessaVencida: {
    titulo: 'Promessa vencida', curto: 'Promessa vencida', icone: 'alerta', tom: 'danger', selo: 'promessa vencida',
    sub: 'O cliente prometeu, o prazo acabou e o pagamento não entrou. A cobrança já voltou.',
    vazio: 'Nenhuma promessa vencida.',
  },
  boletoVivo: {
    titulo: 'Boleto vivo vencido', curto: 'Boleto vivo', icone: 'document', tom: 'warning', selo: 'boleto vivo',
    sub: 'O título continua válido no banco, e o cliente consegue pagar hoje.',
    vazio: 'Nenhuma vencida com boleto vivo.',
  },
  orgao: {
    titulo: 'Órgão público', curto: 'Órgão público', icone: 'predio', tom: 'warning', selo: 'órgão',
    sub: 'Paga quando o processo anda. Quem destrava é o fiscal do contrato.',
    vazio: 'Nenhum órgão público vencido.',
  },
  tedPrivado: {
    titulo: 'Privado por TED', curto: 'Privado por TED', icone: 'link', tom: 'warning', selo: 'TED privado',
    sub: 'Empresa privada que paga por transferência. Cobrança por e-mail, como as outras empresas.',
    vazio: 'Nenhuma empresa privada por TED vencida.',
  },
  baixado: {
    titulo: 'Boleto baixado, sem pausa', curto: 'Boleto baixado', icone: 'ban', tom: null, selo: 'baixado',
    sub: 'Cobrado por fora. Pausar com motivo e data tira do alerta diário até a revisão.',
    vazio: 'Nenhum boleto baixado sem pausa.',
  },
  pausada: {
    titulo: 'Em negociação ou pausada', curto: 'Pausada', icone: 'clock', tom: null, selo: 'pausada',
    sub: 'Ninguém cobra até a data. Na data, volta sozinha para o grupo de origem.',
    vazio: 'Nenhuma pausada.',
  },
  outras: {
    titulo: 'Outras vencidas', curto: 'Outras', icone: 'pacote', tom: 'warning', selo: 'vencida',
    sub: 'Vencidas sem boleto vivo, fora dos outros motivos.',
    vazio: 'Nenhuma.',
  },
};

/** A ação do próximo passo. `contatos` está em toda linha, à parte. */
export type Acao = 'reenviar' | 'abrirFatura' | 'cobrar' | 'contato' | 'pausar' | 'retomar';

export interface Papel {
  /** ADMIN: Cobrar e Pausar/Retomar são `@Roles(ADMIN)` no backend. */
  admin: boolean;
  /** ADMIN e EDITOR: registrar contato, carimbar promessa, reenviar. */
  registra: boolean;
}

/** O papel de quem está logado, pelo `user` do localStorage (o mesmo que o App lê). Sem leitura, nada de escrita. */
export const papelNaCobranca = (): Papel => {
  try {
    const role = JSON.parse(localStorage.getItem('user') || '{}')?.role;
    return { admin: role === 'admin', registra: role === 'admin' || role === 'editor' };
  } catch {
    return { admin: false, registra: false };
  }
};

const dia = fmtDate;
const reais = fmtCurrency;
/** Quantas vezes já foi cobrada sai no SELO (`seloDaCobranca`); o detalhe só fala da que nunca foi. */
const vezes = (n?: number) => (!n ? 'Nenhuma cobrança registrada.' : '');

/**
 * O selo da fatura já cobrada: "Cobrada 2× · 16/10", com a data da ÚLTIMA cobrança no dia de Brasília. Nunca cobrada,
 * sem selo. Pedido do Heitor (30/10/2026): o "Cobrada N vezes" em cinza no meio da frase não se via.
 */
export function seloDaCobranca(l: Pick<LinhaDaCobranca, 'dunningCount' | 'lastDunningAt'>): string | null {
  const n = Number(l.dunningCount) || 0;
  if (n <= 0) return null;
  const quando = l.lastDunningAt ? fmtDate(l.lastDunningAt).slice(0, 5) : '';
  return `Cobrada ${n}×${quando && quando !== '—' ? ` · ${quando}` : ''}`;
}

/** O próximo passo da linha, em palavras, e as ações que o papel de quem vê permite. */
export function proximoPasso(motivo: Motivo, l: LinhaDaCobranca, papel: Papel): { passo: string; detalhe: string; acoes: Acao[] } {
  const cobrarOuContato: Acao[] = papel.admin ? ['cobrar'] : papel.registra ? ['contato'] : [];
  switch (motivo) {
    case 'email':
      return { passo: 'Reenviar para outro contato.', detalhe: 'O e-mail da cobrança voltou.', acoes: papel.registra ? ['reenviar'] : [] };
    case 'semNota':
      return {
        passo: 'Emitir a nota fiscal.',
        detalhe: 'Sem nota autorizada, o cliente não tem como pagar. Cliente com NF Manual emite pelo NF parcial, na fatura.',
        acoes: ['abrirFatura'],
      };
    case 'promessaVencida': {
      const p = l.promessaVencida!;
      return {
        passo: 'A promessa não foi cumprida.',
        detalhe: `Prometeu para ${dia(p.date)}${p.amount != null ? ` (${reais(p.amount)})` : ''}, e o prazo acabou sem pagamento.`,
        acoes: [...(papel.registra ? ['contato' as Acao] : []), ...(papel.admin ? ['cobrar' as Acao] : [])],
      };
    }
    case 'pausada':
      if (l.blockedKind === 'promessa') {
        return {
          passo: `Promessa para ${dia(l.promisedDate)}${l.promisedAmount != null ? ` de ${reais(l.promisedAmount)}` : ''}.`,
          detalhe: `A cobrança volta em ${dia(l.blockedUntil)} (2 dias úteis de compensação).`,
          acoes: [],
        };
      }
      return {
        passo: `Pausada até ${dia(l.blockedUntil || l.snoozedUntil)}${l.snoozeReason ? ':' : '.'}`,
        detalhe: l.snoozeReason || '',
        acoes: papel.admin ? ['retomar'] : [],
      };
    case 'boletoVivo':
      return { passo: 'Cobrar.', detalhe: vezes(l.dunningCount), acoes: cobrarOuContato };
    // ⚠️ Órgão e baixado também têm "Cobrar" para admin, como segunda ação: em Faturas o admin cobrava qualquer vencida,
    // e a unificação (E1) não pode tirar isso. A régua de hoje já cobrou baixado por e-mail (UNUS, Apolo, J.L.).
    case 'tedPrivado':
      return { passo: 'Cobrar por e-mail.', detalhe: vezes(l.dunningCount), acoes: cobrarOuContato };
    case 'orgao':
      return {
        passo: 'Falar com o fiscal do contrato.',
        detalhe: vezes(l.dunningCount),
        acoes: [...(papel.registra ? ['contato' as Acao] : []), ...(papel.admin ? ['cobrar' as Acao] : [])],
      };
    case 'baixado':
      return { passo: 'Pausar com motivo e data de revisão.', detalhe: `Boleto baixado. ${vezes(l.dunningCount)}`, acoes: papel.admin ? ['pausar', 'cobrar'] : [] };
    default:
      return { passo: 'Cobrar.', detalhe: vezes(l.dunningCount), acoes: cobrarOuContato };
  }
}

/**
 * O último dia em que a promessa segura a cobrança: a data + 2 dias úteis (segunda a sexta, SEM feriado).
 *
 * ⛔ É ESPELHO de `prazoDaPromessa` do backend (helpers/prazo-da-promessa.util.ts), só para a frase do formulário dizer
 * a data ANTES de salvar. Quem decide é o servidor: depois de salvar, a tela mostra o `dunningPausedUntil` que ele
 * devolveu, e não esta conta.
 */
export function prazoDaPromessa(promisedDate: string | null | undefined): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(promisedDate || ''));
  if (!m) return null;
  let ms = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (!Number.isFinite(ms)) return null;
  let restantes = 2;
  while (restantes > 0) {
    ms += 86_400_000;
    const d = new Date(ms).getUTCDay();
    if (d !== 0 && d !== 6) restantes--;
  }
  const r = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${r.getUTCFullYear()}-${p(r.getUTCMonth() + 1)}-${p(r.getUTCDate())}`;
}

/**
 * FAIXAS DE ATRASO (K1, decisão do Heitor de 02/11/2026): 1-30, 31-60, 61-90 e 90+. A barra da Cobrança recorta todos
 * os grupos por elas, e o cartão "Inadimplência por faixa" do Dashboard usa as mesmas, abrindo a Cobrança já filtrada.
 *
 * ⛔ A RÉGUA É DO SERVIDOR (backend #587): as chaves são as do `aging` de `GET /invoices/dunning-candidates/summary`, e
 * a faixa de cada linha vem pronta em `faixaDeAtraso`. A tela não tem fronteira nenhuma: recalcular de `daysOverdue`
 * aqui seria a segunda cópia da régua, o defeito que essa decisão conserta. A chave vai na URL (`?atraso=dias90mais`).
 */
export type FaixaDeAtraso = 'dias1a30' | 'dias31a60' | 'dias61a90' | 'dias90mais';

/** Só nomes, na ordem do atraso. `rotulo` na barra da Cobrança, `extenso` no cartão do Dashboard. */
export const FAIXAS_DE_ATRASO: Array<{ chave: FaixaDeAtraso; rotulo: string; extenso: string }> = [
  { chave: 'dias1a30', rotulo: '1-30', extenso: '1 a 30 dias' },
  { chave: 'dias31a60', rotulo: '31-60', extenso: '31 a 60 dias' },
  { chave: 'dias61a90', rotulo: '61-90', extenso: '61 a 90 dias' },
  { chave: 'dias90mais', rotulo: '90+', extenso: 'mais de 90 dias' },
];

/** Quantidade e saldo em aberto (`originalValue`, não `amountDue`: o resumo é recebível, não o que a cobrança pede). */
export interface ContagemDaFila { count: number; value: number }

/**
 * O resumo da fila, da MESMA consulta da lista. `aReceber` e `vencidas` são iguais hoje (a fila só traz vencidas), mas
 * são dois campos de propósito: se a fila passar a trazer pré-vencimento, o cabeçalho não mente sozinho.
 */
export interface ResumoDaFila {
  aReceber: ContagemDaFila;
  vencidas: ContagemDaFila;
  aging: Record<FaixaDeAtraso, ContagemDaFila>;
}

/**
 * O resumo, se tem a forma do contrato; senão null. O servidor promete as quatro chaves sempre, mas uma resposta fora da
 * forma (rota antiga, proxy, erro com corpo) não pode derrubar a tela: vira "não leu", e quem chama mostra o "—".
 */
export function resumoDaFilaValido(r: any): ResumoDaFila | null {
  const ok = (c: any) => !!c && Number.isFinite(Number(c.count)) && Number.isFinite(Number(c.value));
  if (!r || typeof r !== 'object' || !ok(r.aReceber) || !ok(r.vencidas) || !r.aging) return null;
  return FAIXAS_DE_ATRASO.every((f) => ok(r.aging[f.chave])) ? (r as ResumoDaFila) : null;
}

/** O parâmetro da URL que guarda a faixa escolhida na Cobrança. */
export const PARAMETRO_DA_FAIXA = 'atraso';

/** A faixa pedida na query, ou null para valor ausente ou desconhecido. */
export function faixaDaUrl(search: string): FaixaDeAtraso | null {
  const v = new URLSearchParams(search || '').get(PARAMETRO_DA_FAIXA);
  return FAIXAS_DE_ATRASO.some((f) => f.chave === v) ? (v as FaixaDeAtraso) : null;
}
